export const publicPartArticle = {
    id: 'public-part',
    title: 'Публичный режим',
    category: 'Основы',
    requiredModule: null,
    summary: 'Как устроена публичная интерактивная карта для клиентов, инвесторов и посетителей.',
    markdown: `
# Публичная карта (Режим просмотра)

> **Публичный режим** — это открытая электронная витрина вашей территории. Сюда заходят покупатели, инвесторы и гости, чтобы изучить свободные участки, посмотреть спутниковые снимки и фотографии объектов без регистрации и паролей.

[🌐 Открыть публичную карту территории](/map/)

---

## 1. Безопасность: здесь нельзя ничего сломать

Публичная карта доступна по прямой ссылке ([/map/](/map/)) любому посетителю. При этом система полностью защищена:

:::shield Спокойствие за данные проекта
* **Только просмотр:** Инструменты рисования, стирания и перемещения границ на этой странице полностью отключены. Гость может нажимать любые кнопки — данные проекта останутся в полной сохранности.
* **Закрытая служебная информация:** В карточках отображаются только публичные параметры (площадь, статус, фотографии, цена). Внутренние заметки сотрудников, телефоны клиентов и закрытые договоры на публичную карту никогда не попадают.
:::

---

## 2. Три вида карты: как переключать фон

В правом верхнем углу находится кнопка смены подложки. Она позволяет взглянуть на территорию в трёх ракурсах:

:::grid cols=3
:::card icon="map" title="Схема"
Чёткая векторная карта. На ней хорошо видны контуры участков, названия улиц, дорожная разметка и номера домов. Идеальна для быстрого поиска.
:::
:::card icon="satellite" title="Спутник"
Реальные спутниковые снимки высокой чёткости. Позволяют оценить местность: где растут деревья, где протекает река и как выглядит площадка из космоса.
:::
:::card icon="layers" title="Генплан (Свой слой)"
Проектная схема застройки, наложенная прямо поверх местности. С помощью ползунка прозрачности можно сопоставить проект с реальной землёй.
:::
:::

Дополнительно на карте включены **объёмные 3D-здания** и понятная штриховка зон. Карту можно не только приближать, но и наклонять (зажмите правую кнопку мыши), чтобы увидеть посёлок в перспективе.

---

## 3. Умная панель фильтров (Легенда)

Панель легенды в левом верхнем углу формируется **автоматически на основе данных из «Менеджера легенды»** административной панели. Любые категории, созданные сотрудниками, сразу становятся доступными фильтрами для посетителей.

Легенда объединяет динамические категории объектов и коммерческие статусы:

:::grid cols=2
:::card icon="layers" title="Слои из Менеджера легенды"
* **Участки:** группировка по функциональному назначению (жилые, коммерческие, рекреация) с фирменными цветами.
* **Здания:** типы строений (коттеджи, таунхаусы, объекты инфраструктуры).
* **Линии и дороги:** магистрали, внутренние проезды, пешеходные дорожки.
* **Метки (POI):** КПП, детские площадки, парковки и объекты сервиса с загруженными иконками.
:::
:::card icon="click" title="4 коммерческих статуса"
* 🟢 **В продаже** — свободные участки и здания, доступные для покупки или аренды.
* 🟡 **Строится** — объекты, на которых ведутся строительные работы.
* 🔵 **В планах** — перспективные очереди застройки.
* ⚪ **Без статуса** — общественные, технические и вспомогательные зоны.
:::
:::

Для каждой категории доступны кнопки **«Все»** и **«Сброс»** для мгновенной фильтрации, а также общий переключатель показа цветных иконок статусов на полигонах карты.

---

## 4. Карточка объекта: что происходит при клике

Когда посетитель нажимает на любой участок или здание на карте:

1. **Плавный подлёт:** Камера сама центрируется на объекте за 1 секунду.
2. **Янтарная подсветка:** Выбранный контур выделяется яркой жёлто-оранжевой рамкой.
3. **Открытие карточки:** Сбоку появляется аккуратный паспорт объекта.

:::card icon="file-text" title="Что видит покупатель в карточке:"
* **Галерею фотографий:** снимки местности, видовые точки и фото строительства с подписями.
* **Главные параметры:** номер участка, точную площадь в сотках или м² и статус готовности.
* **Полезные характеристики:** коммуникации (электричество, газ, вода), особенности рельефа.
* **Кнопку действия:** прямую ссылку на скачивание буклета или переход к форме бронирования.
:::

> **Полезная мелочь:** Когда пользователь закрывает карточку (крестиком или клавишей \`Esc\`), камера автоматически возвращается ровно в то место и на ту высоту, где он находился до клика.

---

## 5. Удобные ссылки для отправки клиентам (Deep Linking)

Вам больше не нужно объяснять покупателю по телефону: *«Найдите там в третьем ряду слева от леса небольшой квадрат»*. Система умеет делиться точным видом карты:

* **Ссылка на конкретный участок:** У каждого объекта есть свой адрес. Менеджер может скопировать прямую ссылку из карточки и отправить её клиенту в WhatsApp, Telegram или прикрепить к письму. При переходе по ссылке карта сама откроется, подлетит к нужному участку и распахнёт его карточку с фотографиями.
* **Ссылка на выборку по фильтрам:** Выбрали в легенде «Только свободные участки у воды»? Ссылка в строке браузера обновилась автоматически. Отправьте её клиенту — он увидит ровно ту же настроенную картину.

:::tip Сценарий: Как отправить лот клиенту за 2 шага
1. **Шаг 1:** Кликните по нужному участку на карте (или выберите статус в легенде).
2. **Шаг 2:** Скопируйте ссылку из адресной строки браузера и отправьте клиенту.
* **Результат:** Покупатель откроет ссылку и сразу увидит выбранный объект в фокусе внимания.
:::

---

## 6. Просмотр со смартфонов и планшетов

Карта полностью адаптирована для мобильных устройств:
* **Выдвижная шторка:** Карточка объекта на телефоне плавно выезжает снизу вверх и не перекрывает весь экран. Её можно смахнуть пальцем вниз в любой момент.
* **Удобное управление пальцами:** Карта плавно масштабируется жестом «щепотка» двумя пальцами и отличает случайное касание при прокрутке страницы от точного нажатия на участок.
* **Сворачиваемая легенда:** Панель фильтров на смартфонах прячется в аккуратную круглую кнопку, чтобы освободить экран для обзора территории.
`
};
